<script lang="ts">
  import { onMount } from 'svelte';
  import Button from '../shared/Button.svelte';
  import Icon from '../icons/Icon.svelte';
  import BrandIcon from '../shared/BrandIcon.svelte';
  import TimeCrystalDemo from './TimeCrystalDemo.svelte';
  import ShaderCanvas from '../effects/ShaderCanvas.svelte';
  import heroShader from '$lib/shaders/hero-noise.wgsl?raw';

  let pointerX = $state(0.5);
  let pointerY = $state(0.5);
  let time = $state(0);
  let mounted = $state(false);
  let animId = 0;

  const uniforms = $derived(new Float32Array([time, 1.0, pointerX, pointerY]));

  onMount(() => {
    mounted = true;
    const start = performance.now();
    function tick() {
      time = (performance.now() - start) / 1000;
      animId = requestAnimationFrame(tick);
    }
    tick();
    return () => cancelAnimationFrame(animId);
  });

  function onPointerMove(e: PointerEvent) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    pointerX = (e.clientX - rect.left) / rect.width;
    pointerY = (e.clientY - rect.top) / rect.height;
  }
</script>

<section class="hero" onpointermove={onPointerMove}>
  <div class="hero-bg" aria-hidden="true">
    {#if mounted}
      <ShaderCanvas shader={heroShader} {uniforms}>
        {#snippet fallback()}<div class="hero-bg-fallback"></div>{/snippet}
      </ShaderCanvas>
    {/if}
  </div>
  <div class="wrap">
    <div class="hero-grid">
      <div class="hero-text">
        <div class="hero-icon">
          <BrandIcon size={56} />
        </div>
        <div class="hero-eyebrow">
          <span class="hero-eyebrow-dot" aria-hidden="true"></span>
          <span>VU Labs · Authenticator · Level 0</span>
        </div>
        <h1 class="h-display">
          Codes that <em>come to you.</em>
        </h1>
        <p class="hero-lede lede">
          VuAuth knows what site you're logging into. The right code surfaces. The clock no longer
          runs out. Nothing leaves your device — ever.
        </p>
        <div class="hero-cta">
          <Button href="/demo" variant="primary" size="lg">
            Launch the app
            <Icon name="arrow-right" size={16} />
          </Button>
          <Button href="#level0" variant="ghost" size="lg">See the math</Button>
        </div>
        <div class="hero-stats">
          <div>
            <div class="hero-stat-num"><em>0</em> packets</div>
            <div class="hero-stat-lbl">sent today, ever</div>
          </div>
          <div>
            <div class="hero-stat-num"><em>0</em> origins</div>
            <div class="hero-stat-lbl">third-party at source</div>
          </div>
          <div>
            <div class="hero-stat-num">RFC 6238</div>
            <div class="hero-stat-lbl">5/5 vectors verified</div>
          </div>
        </div>
      </div>
      <TimeCrystalDemo />
    </div>
  </div>
</section>

<style>
  .hero {
    position: relative;
    padding: 72px 0 96px;
    overflow: hidden;
  }
  @media (max-width: 640px) {
    .hero {
      padding: 48px 0 64px;
    }
  }
  .hero-bg {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
  }
  :global([data-theme='modern']) .hero-bg::before {
    content: '';
    position: absolute;
    top: -200px;
    left: 50%;
    transform: translateX(-50%);
    width: 1100px;
    height: 1100px;
    background: radial-gradient(
      circle at center,
      rgba(0, 212, 255, 0.18) 0%,
      rgba(0, 212, 255, 0.06) 30%,
      transparent 60%
    );
    filter: blur(40px);
  }
  :global([data-theme='modern']) .hero-bg::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(to right, rgba(255, 255, 255, 0.04) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
    background-size: 56px 56px;
    mask-image: radial-gradient(ellipse 80% 60% at 50% 40%, #000 30%, transparent 75%);
    -webkit-mask-image: radial-gradient(ellipse 80% 60% at 50% 40%, #000 30%, transparent 75%);
  }
  .hero-bg-fallback {
    position: absolute;
    inset: 0;
  }
  :global([data-theme='modern']) .hero-bg-fallback::before {
    content: '';
    position: absolute;
    top: -200px;
    left: 50%;
    transform: translateX(-50%);
    width: 1100px;
    height: 1100px;
    background: radial-gradient(
      circle at center,
      rgba(0, 212, 255, 0.18) 0%,
      rgba(0, 212, 255, 0.06) 30%,
      transparent 60%
    );
    filter: blur(40px);
  }
  :global([data-theme='brutalist']) .hero-bg {
    background-image:
      linear-gradient(to right, var(--text) 1px, transparent 1px),
      linear-gradient(to bottom, var(--text) 1px, transparent 1px);
    background-size: 80px 80px;
    opacity: 0.06;
  }
  .wrap {
    max-width: var(--max-w);
    margin: 0 auto;
    padding: 0 var(--gutter);
    position: relative;
    z-index: 1;
  }
  @media (max-width: 640px) {
    .wrap {
      padding: 0 var(--gutter-sm);
    }
  }
  .hero-grid {
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    gap: 64px;
    align-items: center;
  }
  @media (max-width: 960px) {
    .hero-grid {
      grid-template-columns: 1fr;
      gap: 48px;
    }
  }
  .hero-icon {
    margin-bottom: 20px;
  }
  .hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-2);
    margin-bottom: 24px;
  }
  .hero-eyebrow-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--good);
    box-shadow: 0 0 12px var(--good);
    animation: pulse 2s ease-in-out infinite;
  }
  :global([data-theme='brutalist']) .hero-eyebrow-dot {
    border-radius: 0;
    box-shadow: none;
  }
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.45;
    }
  }
  .h-display {
    font-size: clamp(40px, 7vw, 88px);
    font-weight: 700;
    letter-spacing: -0.04em;
    line-height: 0.98;
    margin-bottom: 24px;
  }
  .h-display em {
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    color: var(--accent);
    letter-spacing: -0.01em;
  }
  :global([data-theme='brutalist']) .h-display {
    font-weight: 900;
    letter-spacing: -0.05em;
  }
  :global([data-theme='brutalist']) .h-display em {
    color: var(--text);
    font-weight: 400;
  }
  .hero-lede {
    margin-bottom: 36px;
    max-width: 540px;
  }
  .lede {
    font-size: clamp(16px, 1.7vw, 20px);
    color: var(--text-2);
    line-height: 1.55;
  }
  .hero-cta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 32px;
  }
  .hero-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 32px;
    padding-top: 24px;
    border-top: 1px solid var(--border);
  }
  :global([data-theme='brutalist']) .hero-stats {
    border-top: 2px solid var(--text);
  }
  .hero-stat-num {
    font-size: 22px;
    font-weight: 800;
    color: var(--text);
    letter-spacing: -0.02em;
  }
  :global([data-theme='brutalist']) .hero-stat-num {
    font-weight: 900;
  }
  .hero-stat-num em {
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    color: var(--accent);
  }
  :global([data-theme='brutalist']) .hero-stat-num em {
    color: var(--text);
  }
  .hero-stat-lbl {
    font-size: 12px;
    color: var(--text-3);
    margin-top: 2px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
</style>
